/**
 * BuildupEditor - 빌드업(단계별 학습) 편집 모달
 *
 * 스텝 추가/삭제, 아이템 추가/삭제/편집, 이미지 URL 설정.
 */
import { useState, useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  X, Plus, Trash2, Loader2, Save, ChevronDown, ChevronUp,
  GripVertical, Image as ImageIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { quetureContentApi } from '@/api/quetureContent'
import type { BuildupStep, BuildupItem } from '@/api/quetureContent'

const DEFAULT_ITEM: BuildupItem = {
  number: 1,
  instruction: '',
  instructionImageUrl: null,
  options: null,
  answer: null,
  explanation: null,
  explanationImageUrl: null,
  teacherCheck: null,
}

const DEFAULT_STEP: BuildupStep = {
  step: 'STEP 1',
  title: '',
  items: [{ ...DEFAULT_ITEM }],
}

interface Props {
  quetureId: string
  onClose: () => void
}

export function BuildupEditor({ quetureId, onClose }: Props) {
  const queryClient = useQueryClient()
  const [steps, setSteps] = useState<BuildupStep[]>([])
  const [loaded, setLoaded] = useState(false)
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([0]))

  const { data: fetchedData, isLoading } = useQuery({
    queryKey: ['buildup', quetureId],
    queryFn: () => quetureContentApi.getBuildup(quetureId),
  })

  useEffect(() => {
    if (fetchedData && !loaded) {
      setSteps(fetchedData.length > 0 ? fetchedData : [{ ...DEFAULT_STEP }])
      setExpandedSteps(new Set([0]))
      setLoaded(true)
    }
  }, [fetchedData, loaded])

  const saveMutation = useMutation({
    mutationFn: () => quetureContentApi.saveBuildup(quetureId, steps),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      toast.success('빌드업 저장 완료')
      onClose()
    },
    onError: () => toast.error('저장 실패'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => quetureContentApi.deleteBuildup(quetureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      toast.success('빌드업 삭제 완료')
      onClose()
    },
    onError: () => toast.error('삭제 실패'),
  })

  // ── Step helpers ───────────────────────────────────────────────────

  const toggleStep = useCallback((idx: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }, [])

  const addStep = useCallback(() => {
    const num = steps.length + 1
    setSteps((prev) => [...prev, { step: `STEP ${num}`, title: '', items: [{ ...DEFAULT_ITEM }] }])
    setExpandedSteps((prev) => new Set(prev).add(steps.length))
  }, [steps.length])

  const removeStep = useCallback((idx: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== idx))
  }, [])

  const updateStep = useCallback((idx: number, field: 'step' | 'title', value: string) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)))
  }, [])

  // ── Item helpers ───────────────────────────────────────────────────

  const addItem = useCallback((stepIdx: number) => {
    setSteps((prev) =>
      prev.map((s, i) =>
        i === stepIdx
          ? { ...s, items: [...s.items, { ...DEFAULT_ITEM, number: s.items.length + 1 }] }
          : s
      )
    )
  }, [])

  const removeItem = useCallback((stepIdx: number, itemIdx: number) => {
    setSteps((prev) =>
      prev.map((s, i) =>
        i === stepIdx
          ? { ...s, items: s.items.filter((_, j) => j !== itemIdx).map((it, j) => ({ ...it, number: j + 1 })) }
          : s
      )
    )
  }, [])

  const updateItem = useCallback(
    (stepIdx: number, itemIdx: number, field: keyof BuildupItem, value: string | null) => {
      setSteps((prev) =>
        prev.map((s, si) =>
          si === stepIdx
            ? {
                ...s,
                items: s.items.map((it, ii) =>
                  ii === itemIdx ? { ...it, [field]: value || null } : it
                ),
              }
            : s
        )
      )
    },
    []
  )

  if (isLoading || !loaded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl p-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">빌드업 설정</h2>
            <p className="text-sm text-gray-500 font-mono">{quetureId}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {steps.map((step, stepIdx) => (
            <div key={stepIdx} className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
              {/* Step header */}
              <div
                className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-gray-100"
                onClick={() => toggleStep(stepIdx)}
              >
                <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
                {expandedSteps.has(stepIdx) ? (
                  <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                )}
                <span className="text-sm font-semibold text-indigo-600 shrink-0">{step.step}</span>
                <span className="text-sm text-gray-600 truncate flex-1">
                  {step.title || '(제목 없음)'}
                </span>
                <span className="text-xs text-gray-400 shrink-0">{step.items.length}개 항목</span>
                {steps.length > 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeStep(stepIdx) }}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Step body (expanded) */}
              {expandedSteps.has(stepIdx) && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-200 bg-white">
                  {/* Step name & title */}
                  <div className="grid grid-cols-[120px_1fr] gap-3 pt-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600">단계명</label>
                      <input
                        type="text"
                        value={step.step}
                        onChange={(e) => updateStep(stepIdx, 'step', e.target.value)}
                        className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">제목 (번호 '-' 행)</label>
                      <input
                        type="text"
                        value={step.title}
                        onChange={(e) => updateStep(stepIdx, 'title', e.target.value)}
                        placeholder="[핵심 키워드 찾기] 깨처에서 배운 핵심 단어를 적어보세요."
                        className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Items */}
                  {step.items.map((item, itemIdx) => (
                    <div key={itemIdx} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50/50">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-500">#{item.number}</span>
                        {step.items.length > 1 && (
                          <button
                            onClick={() => removeItem(stepIdx, itemIdx)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {/* 지시문/문제 + 이미지 */}
                      <div>
                        <label className="text-xs font-medium text-gray-600">지시문/문제</label>
                        <textarea
                          value={item.instruction ?? ''}
                          onChange={(e) => updateItem(stepIdx, itemIdx, 'instruction', e.target.value)}
                          rows={2}
                          placeholder="지시문 또는 문제 텍스트"
                          className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <div className="flex items-center gap-1.5 mt-1">
                          <ImageIcon className="w-3.5 h-3.5 text-gray-400" />
                          <input
                            type="text"
                            value={item.instructionImageUrl ?? ''}
                            onChange={(e) => updateItem(stepIdx, itemIdx, 'instructionImageUrl', e.target.value)}
                            placeholder="이미지 URL (선택)"
                            className="flex-1 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>

                      {/* 보기 */}
                      <div>
                        <label className="text-xs font-medium text-gray-600">보기</label>
                        <textarea
                          value={item.options ?? ''}
                          onChange={(e) => updateItem(stepIdx, itemIdx, 'options', e.target.value)}
                          rows={1}
                          placeholder="선택지 또는 참고자료"
                          className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>

                      {/* 정답 */}
                      <div>
                        <label className="text-xs font-medium text-gray-600">정답/모범답안</label>
                        <textarea
                          value={item.answer ?? ''}
                          onChange={(e) => updateItem(stepIdx, itemIdx, 'answer', e.target.value)}
                          rows={1}
                          placeholder="정답"
                          className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>

                      {/* 해설 + 이미지 */}
                      <div>
                        <label className="text-xs font-medium text-gray-600">해설 및 정답 기준</label>
                        <textarea
                          value={item.explanation ?? ''}
                          onChange={(e) => updateItem(stepIdx, itemIdx, 'explanation', e.target.value)}
                          rows={1}
                          placeholder="해설 또는 채점 기준"
                          className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <div className="flex items-center gap-1.5 mt-1">
                          <ImageIcon className="w-3.5 h-3.5 text-gray-400" />
                          <input
                            type="text"
                            value={item.explanationImageUrl ?? ''}
                            onChange={(e) => updateItem(stepIdx, itemIdx, 'explanationImageUrl', e.target.value)}
                            placeholder="해설 이미지 URL (선택)"
                            className="flex-1 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>

                      {/* 선생님 체크 */}
                      <div>
                        <label className="text-xs font-medium text-gray-600">선생님 체크</label>
                        <input
                          type="text"
                          value={item.teacherCheck ?? ''}
                          onChange={(e) => updateItem(stepIdx, itemIdx, 'teacherCheck', e.target.value)}
                          placeholder="선생님 확인 사항"
                          className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  ))}

                  {/* 항목 추가 */}
                  <button
                    onClick={() => addItem(stepIdx)}
                    className="flex items-center gap-1.5 px-3 py-1.5 w-full justify-center text-xs border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> 항목 추가
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* 스텝 추가 */}
          <button
            onClick={addStep}
            className="flex items-center gap-1.5 px-4 py-2 w-full justify-center text-sm border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
          >
            <Plus className="w-4 h-4" /> 스텝 추가
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={() => {
              if (confirm('빌드업을 삭제하시겠습니까?')) deleteMutation.mutate()
            }}
            className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
          >
            삭제
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              취소
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              저장
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
